import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, ShoppingBag, Heart, Plus, ArrowUpRight, Star, ShieldCheck, BadgeCheck, Gem, Ruler, Clock3, ChevronLeft, ChevronRight } from "lucide-react";
import { api, imgUrl, money } from "../api";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";
import SizeChartModal from "../components/SizeChartModal";
import ProductDescription from "../components/ProductDescription";
import { normalizeProductSizes, sizeTestId } from "../data/sizing";
import { buildProductBookingBill, formatDeliveryTime } from "../utils/whatsappBill";
import { recordProductInterest } from "../utils/recommendations";
import { buildProductSeo, PRODUCT_FAQS } from "../utils/productSeo";

const ACCORDION = PRODUCT_FAQS;

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, wishlist } = useCart();
  const { settings, whatsappLink } = useSettings();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [openAccordion, setOpenAccordion] = useState("size_fit");
  const [related, setRelated] = useState([]);
  const [sizeChartOpen, setSizeChartOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const galleryTouchStart = useRef(null);

  useEffect(() => {
    setNotFound(false);
    setSize("");
    setSizeChartOpen(false);
    setActiveImageIndex(0);
    (async () => {
      try {
        const { data } = await api.get(`/api/products/${id}`);
        setProduct(data);
        recordProductInterest(data, { strength: 4 });
        setColor(data.colors?.[0] || "");
        // Fetch related products from same category
        const rel = await api.get("/api/products", { params: { category: data.category, limit: 200 } });
        setRelated(rel.data.filter((p) => p.id !== data.id));
      } catch {
        setNotFound(true);
      }
    })();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  if (notFound) {
    return (
      <main className="page">
        <p className="empty" data-testid="product-not-found">Look not found.</p>
      </main>
    );
  }
  if (!product) {
    return (
      <main className="page">
        <p className="empty" data-testid="product-loading">Loading…</p>
      </main>
    );
  }

  const price = product.offer_price ?? product.original_price;
  const originalPrice = product.offer_price ? product.original_price : null;
  const discount =
    product.discount_percentage ||
    (originalPrice && price ? Math.round((1 - price / originalPrice) * 100) : null);
  const wished = wishlist.includes(product.id);
  const uniqueImages = Array.from(new Set([product.main_image, ...(product.images || [])].filter(Boolean)));
  const images = uniqueImages.length ? uniqueImages : [""];
  const activeImage = images[activeImageIndex] || images[0];
  const availableSizes = normalizeProductSizes(product.sizes);
  const rating = product.rating || (product.is_top_rated ? 4.9 : product.is_bestseller ? 4.8 : 4.7);
  const productSeo = buildProductSeo(product, images.map((item) => imgUrl(item)));

  const showImage = (index) => {
    const total = images.length;
    if (total < 2) return;
    setActiveImageIndex((index + total) % total);
  };

  const moveImage = (direction) => {
    if (images.length < 2) return;
    setActiveImageIndex((current) => (current + direction + images.length) % images.length);
  };

  const finishGallerySwipe = (event) => {
    if (galleryTouchStart.current === null) return;
    const distance = event.changedTouches[0].clientX - galleryTouchStart.current;
    galleryTouchStart.current = null;
    if (Math.abs(distance) >= 45) moveImage(distance < 0 ? 1 : -1);
  };

  const handleAdd = () => {
    if (!size) {
      toast.error("Please select a size");
      return;
    }
    addToCart(product, size, color, 1);
    toast.success("Added to cart");
    navigate("/cart");
  };

  const waMessage = buildProductBookingBill({
    brandName: settings.brand_name,
    product,
    size,
    color,
  });

  return (
    <>
      <Seo
        title={productSeo.title}
        description={productSeo.description}
        path={`/product/${product.id}`}
        image={imgUrl(product.main_image || images[0])}
        type="product"
        keywords={productSeo.keywords}
        productPrice={price}
        schema={productSeo.schema}
      />
      <div className="pdp" data-testid="product-page">
        <div
          className="pdp-gallery"
          data-testid="product-gallery"
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft") moveImage(-1);
            if (event.key === "ArrowRight") moveImage(1);
          }}
        >
          <div
            className="pdp-gallery-stage"
            tabIndex={images.length > 1 ? 0 : -1}
            aria-label={`${product.name} image ${activeImageIndex + 1} of ${images.length}`}
            onTouchStart={(event) => { galleryTouchStart.current = event.touches[0].clientX; }}
            onTouchEnd={finishGallerySwipe}
          >
            <img
              key={activeImage}
              className="pdp-gallery-main-image"
              src={imgUrl(activeImage)}
              alt={`${product.name}, designer ${product.subcategory || product.category} from Lalita Soujanya Hyderabad — view ${activeImageIndex + 1}`}
              data-testid="product-main-image"
              onError={(event) => {
                event.currentTarget.src =
                  "https://images.unsplash.com/photo-1610189335693-9e909b33aa32?auto=format&fit=crop&w=1200&q=85";
              }}
            />
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  className="pdp-gallery-arrow previous"
                  onClick={() => moveImage(-1)}
                  aria-label="Show previous product image"
                  data-testid="product-image-previous"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  type="button"
                  className="pdp-gallery-arrow next"
                  onClick={() => moveImage(1)}
                  aria-label="Show next product image"
                  data-testid="product-image-next"
                >
                  <ChevronRight size={20} />
                </button>
                <span className="pdp-gallery-count" aria-hidden="true">
                  {String(activeImageIndex + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
                </span>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className="pdp-gallery-thumbnails" role="tablist" aria-label="Choose product image">
              {images.map((image, index) => (
                <button
                  key={`${image}-${index}`}
                  type="button"
                  role="tab"
                  aria-selected={activeImageIndex === index}
                  className={activeImageIndex === index ? "is-active" : ""}
                  onClick={() => setActiveImageIndex(index)}
                  data-testid={`product-image-thumbnail-${index}`}
                >
                  <img
                    src={imgUrl(image)}
                    alt={`View ${index + 1} of ${product.name}`}
                    loading={index > 4 ? "lazy" : "eager"}
                    onError={(event) => {
                      event.currentTarget.src =
                        "https://images.unsplash.com/photo-1610189335693-9e909b33aa32?auto=format&fit=crop&w=300&q=75";
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="pdp-details">
          <span className="eyebrow">
            {product.category}
          </span>
          <h1 data-testid="detail-product-name">{product.name}</h1>
          <div className="pdp-rating" aria-label={`${rating} out of 5 stars`}>
            <span className="pdp-stars">
              {[1, 2, 3, 4, 5].map((star) => <Star key={star} size={15} fill="currentColor" />)}
            </span>
            <strong>{rating.toFixed(1)}</strong>
            <span>House quality rating</span>
          </div>
          <div className="pdp-price">
            <span className="now" data-testid="detail-product-price">{money(price)}</span>
            {originalPrice && <span className="was" data-testid="detail-original-price">{money(originalPrice)}</span>}
            {discount ? <span className="disc">-{discount}% off</span> : null}
          </div>
          <div className="pdp-delivery" data-testid="delivery-time-notice">
            <Clock3 size={16} aria-hidden="true" />
            <span><strong>Estimated delivery</strong> {formatDeliveryTime(product.delivery_time_days)}</span>
          </div>
          <ProductDescription
            value={product.description}
            className="pdp-desc"
            data-testid="detail-product-description"
          />

          <div className="pdp-trust-grid" aria-label="Product assurances">
            <div><ShieldCheck size={21} /><span><strong>Quality tested</strong><small>Inspected by our atelier</small></span></div>
            <div><BadgeCheck size={21} /><span><strong>100% genuine</strong><small>Authentic house product</small></span></div>
            <div><Gem size={21} /><span><strong>Craft assured</strong><small>Made with considered detail</small></span></div>
          </div>

          {product.colors && product.colors.length > 0 && (
            <div className="pdp-section">
              <span className="label">Colour {color && `— ${color}`}</span>
              <div className="color-chips">
                {product.colors.map((c) => (
                  <button
                    key={c}
                    className={`color-chip ${color === c ? "selected" : ""}`}
                    onClick={() => setColor(c)}
                    data-testid={`color-${c.toLowerCase().replace(/\s+/g, "-")}-button`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="pdp-section">
            <div className="pdp-size-heading">
              <span className="label">Size {size && `— ${size}`}</span>
              <button
                type="button"
                className="size-chart-trigger"
                onClick={() => setSizeChartOpen(true)}
                data-testid="size-chart-trigger"
              >
                <Ruler size={15} /> Size chart
              </button>
            </div>
            <div className="size-chips">
              {availableSizes.map((s) => (
                <button
                  type="button"
                  key={s}
                  className={`size-chip ${s === "Custom Tailored" ? "custom" : ""} ${size === s ? "selected" : ""}`}
                  onClick={() => setSize(s)}
                  aria-pressed={size === s}
                  data-testid={`size-${sizeTestId(s)}-button`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="pdp-cta">
            <button className="btn btn-primary btn-full" onClick={handleAdd} data-testid="add-to-cart-button">
              Add to cart <ShoppingBag size={14} />
            </button>
            <a className="btn btn-dark btn-full" href={whatsappLink(waMessage)} target="_blank" rel="noreferrer" data-testid="product-whatsapp-button">
              Order on WhatsApp <MessageCircle size={14} />
            </a>
            <button
              className="btn btn-outline btn-full"
              onClick={() => {
                toggleWishlist(product.id);
                toast.success(wished ? "Removed from wishlist" : "Saved to wishlist");
              }}
              data-testid="product-wishlist-button"
            >
              {wished ? "Remove from wishlist" : "Add to wishlist"} <Heart size={14} fill={wished ? "currentColor" : "none"} />
            </button>
          </div>

          <div className="accordion" data-testid="product-accordion">
            <div className={`item ${openAccordion === "desc" ? "open" : ""}`}>
              <button className="trigger" onClick={() => setOpenAccordion(openAccordion === "desc" ? "" : "desc")} data-testid="accordion-desc-trigger">
                Description <Plus className="plus" size={18} />
              </button>
              <div className="content"><ProductDescription value={product.description} /></div>
            </div>
            {ACCORDION.map((a) => (
              <div key={a.key} className={`item ${openAccordion === a.key ? "open" : ""}`}>
                <button
                  className="trigger"
                  onClick={() => setOpenAccordion(openAccordion === a.key ? "" : a.key)}
                  data-testid={`accordion-${a.key}-trigger`}
                >
                  {a.label} <Plus className="plus" size={18} />
                </button>
                <div className="content">
                  {a.key === "shipping"
                    ? `This product has an estimated delivery time of ${formatDeliveryTime(product.delivery_time_days)}. The studio will confirm the final dispatch and delivery timeline on WhatsApp.`
                    : a.body}
                </div>
              </div>
            ))}
          </div>

          <p className="pdp-meta-line">
            <b>Studio contact</b>
            <br />
            {settings.whatsapp_number} · {settings.email}
          </p>
        </div>
      </div>

      {related.length > 0 && (
        <section className="related" data-testid="related-products">
          <div className="section-head">
            <div>
              <span className="eyebrow">Also from this chapter</span>
              <h2>You may also love.</h2>
            </div>
            <Link to={`/shop/${product.category_slug || (product.category || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="text-link">
              Shop {product.category} <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="related-product-rail">
            {related.map((p) => (
              <ProductCard product={p} key={p.id} />
            ))}
          </div>
        </section>
      )}
      <SizeChartModal open={sizeChartOpen} onClose={() => setSizeChartOpen(false)} />
    </>
  );
}
